import React, { useEffect, useState } from 'react';
import './MaintenanceMode.css';
import { settingsAPI } from '../services/api';

const MaintenanceMode = ({ message, backDate, onMaintenanceDisabled }) => {
  // Check maintenance mode status only when user interacts (to detect when it's disabled)
  useEffect(() => {
    let checkTimeout = null;

    const checkMaintenanceStatus = async () => {
      try {
        const response = await settingsAPI.checkMaintenanceMode();
        if (response.success && response.data && !response.data.enabled) {
          // Maintenance mode is disabled, reload to show normal site
          window.location.reload();
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      }
    };

    // Check when page becomes visible (user switches back to tab) - single check
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Only check once when tab becomes visible
        checkMaintenanceStatus();
      }
    };

    // Check on first user interaction (click, keypress, touch) - single check with debounce
    const handleUserInteraction = () => {
      if (checkTimeout) {
        return; // Already scheduled a check
      }
      checkTimeout = setTimeout(() => {
        checkMaintenanceStatus();
        checkTimeout = null;
      }, 2000); // Wait 2 seconds after interaction before checking
    };

    // Check on page visibility change (user switches back to tab)
    document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });

    // Check on first user interaction
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });

    return () => {
      if (checkTimeout) {
        clearTimeout(checkTimeout);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  const formatBackDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return dateString;
    }
  };

  const formattedBackDate = formatBackDate(backDate);

  // Try multiple image paths
  const imagePaths = [
    '/maintenance-bird.png',
    `${process.env.PUBLIC_URL || ''}/maintain-birdy.svg`,
    './maintenance-bird.png',
    'maintenance-bird.png'
  ];

  const [imageSrc, setImageSrc] = useState(imagePaths[0]);
  const [imageError, setImageError] = useState(false);

  const handleImageError = (e) => {
    const currentIndex = imagePaths.indexOf(imageSrc);
    if (currentIndex < imagePaths.length - 1) {
      // Try next path
      setImageSrc(imagePaths[currentIndex + 1]);
    } else {
      // All paths failed, show fallback
      setImageError(true);
      e.target.style.display = 'none';
    }
  };

  useEffect(() => {
    // Add class to body to isolate styles
    document.body.classList.add('maintenance-mode-active');
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('maintenance-mode-active');
      document.documentElement.style.overflow = '';
    };
  }, []);

  return (
    <div className="maintenance-mode-container">
      <div className="maintenance-mode-content">
        <div className="maintenance-icon">
          {!imageError ? (
            <img 
              src={imageSrc}
              alt="Under Maintenance" 
              className="maintenance-image"
              onError={handleImageError}
            />
          ) : (
            <div className="maintenance-icon-fallback">
              <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" fill="#667eea" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z"/>
                <path fillRule="evenodd" d="M0 10a8 8 0 1 1 15.547 2.661c-.442 1.253-1.845 1.602-2.932 1.25C11.309 13.488 9.475 13 8 13c-1.474 0-3.31.488-4.615.911-1.087.352-2.49.003-2.932-1.25A7.988 7.988 0 0 1 0 10zm8-7a7 7 0 0 0-6.603 9.329c.203.575.923.876 1.68.63C4.397 12.533 6.358 12 8 12s3.604.532 4.923.96c.757.245 1.477-.056 1.68-.631A7 7 0 0 0 8 3z"/>
              </svg>
            </div>
          )}
        </div>
        <h1 className="maintenance-title">We're Under Maintenance</h1>
        <p className="maintenance-message">
          {message || 'We are currently performing scheduled maintenance. Please check back later.'}
        </p>
        {formattedBackDate && (
          <div className="maintenance-back-date">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              viewBox="0 0 16 16"
              style={{ marginRight: '8px' }}
            >
              <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
            </svg>
            <span>We'll be back on <strong>{formattedBackDate}</strong></span>
          </div>
        )}
        <div className="maintenance-footer">
          <p>Thank you for your patience!</p>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceMode;

